import { useTranslation } from "react-i18next";
import { Ruler, X } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@ui/components/ui/dialog";
import { Button } from "@ui/components/ui/button";

const GENERAL_SIZES = [
  { size: "36", cm: "23.5" },
  { size: "37", cm: "24" },
  { size: "38", cm: "24.5" },
  { size: "39", cm: "25" },
  { size: "40", cm: "25.5" },
  { size: "41", cm: "26.5" },
  { size: "42", cm: "27" },
] as const;

const PRO_SIZES = [
  { size: "36", cm: "22.5" },
  { size: "37", cm: "23" },
  { size: "38", cm: "24" },
  { size: "39", cm: "24.5" },
  { size: "40", cm: "25" },
  { size: "41", cm: "26" },
] as const;

const MENS_SIZES = [
  { size: "41", cm: "26.5" },
  { size: "42", cm: "27" },
  { size: "43", cm: "28" },
  { size: "44", cm: "28.5" },
  { size: "45", cm: "29.5" },
  { size: "46", cm: "30" },
  { size: "47", cm: "30.8" },
] as const;

function SizeTable({
  title,
  sizes,
}: Readonly<{
  title: string;
  sizes: ReadonlyArray<{ size: string; cm: string }>;
}>) {
  const { t } = useTranslation();

  return (
    <section className="border-border-5 bg-background-7/30 rounded-2xl border p-3.5">
      <h3 className="text-text-1 mb-3 px-1 text-sm font-semibold tracking-tight">
        {title}
      </h3>
      <div className="border-border-5 bg-background-7/70 overflow-hidden rounded-xl border">
        <table className="w-full border-separate border-spacing-0 text-sm tabular-nums">
          <thead>
            <tr className="bg-background-4/10">
              <th className="text-text-3 border-border-5 border-b px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.08em] uppercase">
                {t("SingleProductPage.sizeGuide.euSize")}
              </th>
              <th className="text-text-3 border-border-5 border-b px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.08em] uppercase">
                {t("SingleProductPage.sizeGuide.footLength")}
              </th>
            </tr>
          </thead>

          <tbody className="[&>tr:last-child>td]:border-b-0">
            {sizes.map((row) => (
              <tr key={row.size} className="bg-background-7/40">
                <td className="text-text-1 border-border-5 border-b px-4 py-3 font-semibold">
                  {row.size}
                </td>
                <td className="text-text-2 border-border-5 border-b px-4 py-3 text-right">
                  {row.cm} cm
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function SizeGuideDialog() {
  const { t } = useTranslation();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          dark="outline"
          size="small"
          className="mb-6 gap-2"
        >
          <Ruler className="size-4" aria-hidden="true" />
          {t("SingleProductPage.sizeGuide.openButton")}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="h-[min(90vh,680px)] !gap-0 overflow-hidden !p-0 sm:max-w-lg"
        showCloseButton={false}
      >
        <div className="flex h-full min-h-0 flex-col">
          <DialogHeader className="border-border-5 bg-background-7/95 sticky top-0 z-10 border-b px-6 py-5 text-left backdrop-blur-sm">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="text-text-1 flex items-center gap-2">
                <span className="bg-background-4/30 text-text-1 rounded-full p-1.5">
                  <Ruler className="size-4" aria-hidden="true" />
                </span>
                {t("SingleProductPage.sizeGuide.title")}
              </DialogTitle>
              <DialogClose asChild>
                <Button
                  type="button"
                  dark="ghost"
                  size="unPadded"
                  className="rounded-lg p-2 transition-colors hover:bg-neutral-100 hover:border-transparent"
                  aria-label={t("Common.close", "Close")}
                >
                  <X className="size-4" aria-hidden="true" />
                </Button>
              </DialogClose>
            </div>
            <DialogDescription className="text-text-3">
              {t("SingleProductPage.sizeGuide.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-background-7/10 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-5">
            <div className="space-y-5">
              <SizeTable
                title={t("SingleProductPage.sizeGuide.generalTitle")}
                sizes={GENERAL_SIZES}
              />
              <SizeTable
                title={t("SingleProductPage.sizeGuide.proTitle")}
                sizes={PRO_SIZES}
              />
              <SizeTable
                title={t("SingleProductPage.sizeGuide.mensTitle")}
                sizes={MENS_SIZES}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
